import { useMsal } from '@azure/msal-react';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useDisclosure } from '@mantine/hooks';
import useFavourites from '../../hooks/common/useFavourites';
import { useGetLookupTypes } from '../../hooks/common/useGetLookupTypes';
import { IGeneralPractice } from '../../hooks/promises/getGeneralPractices';
import {
  ICreateAppointmentBuilder,
  ICreateAppointmentRequestValidationResult,
} from '../../hooks/useCreateAppointmentRequest';
import ErrorPage from '../ErrorPage';
import LoadingPage from '../LoadingPage';
import useDailyCapacity from '../../hooks/common/useDailyCapacity';
import CapacityErrorModal from './CapacityErrorModal';
import RegisterAppointmentFormHeader from './RegisterAppointmentFormHeader';
import { AttendanceStatus } from './enums';
import CommonButton from '../../components/CommonButton';
import OutcomesSection from './OutcomesSection';

interface IRegisterAppointmentFormProps {
  builder: ICreateAppointmentBuilder;
  onValidateSuccess: () => void;
  validateRequest: () => ICreateAppointmentRequestValidationResult;
  attendanceStatus: AttendanceStatus;
}

export default function RegisterAppointmentForm({
  builder,
  onValidateSuccess,
  validateRequest,
  attendanceStatus,
}: IRegisterAppointmentFormProps) {
  const { instance } = useMsal();
  const { loading, error, result: lookupTypes } = useGetLookupTypes();
  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);
  const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [userClosedModal, setUserClosedModal] = useState(false);

  const {
    favourites,
    error: favouritesError,
    loading: loadingFavourites,
  } = useFavourites();

  const {
    loading: loadingCapacity,
    dailyCapacity,
    error: capacityError,
    addingCapacity,
  } = useDailyCapacity(dayjs(builder.dateOfConsultation).format('YYYY-MM-DD'));

  const [favouritePractices, setFavouritePractices] = useState<
    IGeneralPractice[]
  >([]);

  const [shouldShowResultsFallback, setShouldShowResultsFallback] =
    useState(false);

  const [searchingOutcomes, setSearchingOutcomes] = useState(false);

  const onReviewClick = () => {
    const validationResult = validateRequest();

    if (validationResult.success) onValidateSuccess();
    else {
      setValidationErrors(validationResult.validationMessages);
    }
  };

  useEffect(
    () => setFavouritePractices(favourites?.map((x) => x.organisation) ?? []),
    [favourites]
  );

  useEffect(
    () => builder.setFcpName(instance.getActiveAccount()?.name ?? ''),
    []
  );

  useEffect(() => {
    const capacityisZero =
      loadingCapacity === false &&
      dailyCapacity &&
      Number(dailyCapacity.capacityCount) <= 0;

    const shouldShowModal = capacityisZero && !userClosedModal;

    if (!shouldShowModal) {
      closeModal();
    }

    if (shouldShowModal) {
      openModal();
    }
  }, [loadingCapacity, userClosedModal]);

  if (loading || loadingFavourites || loadingCapacity || addingCapacity)
    return <LoadingPage />;
  if (error || favouritesError || capacityError) return <ErrorPage />;

  return (
    <>
      <CapacityErrorModal
        close={closeModal}
        opened={opened}
        setUserClosedModal={setUserClosedModal}
      />
      <div className="pb-4">
        <RegisterAppointmentFormHeader
          builder={builder}
          dailyCapacityCount={dailyCapacity?.capacityCount}
          favouritePractices={favouritePractices}
          lookupTypes={lookupTypes}
          setUserClosedModal={setUserClosedModal}
        />
        {attendanceStatus === AttendanceStatus.Attended && (
          <>
            <div className="flex flex-col items-center gap-6">
              <p className="py-2 text-center text-subtitle">
                A reminder that a successful service is to reduce GP workload
                and to only refer patients on to other clinical services or
                investigations when clinically indicated.
              </p>
              <h1 className="text-3xl">Primary consultation outcomes</h1>
            </div>
            <OutcomesSection
              outcomeList={lookupTypes.consultationOutcomes}
              capacityCount={dailyCapacity?.capacityCount}
              shouldShowResultsFallback={shouldShowResultsFallback}
              setShouldShowResultsFallback={setShouldShowResultsFallback}
              searchingOutcomes={searchingOutcomes}
              setSearchingOutcomes={setSearchingOutcomes}
              selectedOutcomes={builder.consultationOutcomes}
              setSelectedOutcomes={builder.setConsultationOutcomes}
              otherText={builder.customConsultationOutcome}
              setOtherText={builder.setCustomConsultationOutcome}
            />
          </>
        )}
        {validationErrors.length > 0 && (
          <div className="w-[600px] mx-auto mt-4 text-red-500">
            <p className="font-semibold text-center">
              Please address the following feedback before continuing:
            </p>
            <ul className="px-20 ml-24 list-disc">
              {validationErrors.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        )}
        {shouldShowResultsFallback || searchingOutcomes ? null : (
          <div className="flex flex-row justify-center mt-12">
            <CommonButton
              text="Review"
              variant="filled"
              onClick={onReviewClick}
              disabled={!dailyCapacity?.capacityCount}
            />
          </div>
        )}
      </div>
    </>
  );
}
